/**
 * "The detailed client record" walkthrough.
 *
 * Walks through every section of a client's detail page so the user
 * understands what each block of the long-scrolling page is for.
 *
 * Starts on the Clients list, prompts the user to open any client (the
 * tour advances when the detail-page section nav appears), then steps
 * down through the sections in scroll order. The Enhanced CDD section
 * is conditional (only renders for high-risk clients), so its step is
 * gated with showOn so non-EnCDD clients silently skip it.
 *
 * Targets:
 *   tr.clickable                          — any client row (list view)
 *   data-tour="section-tab-overview"      — the section-nav button that
 *                                            appears once the detail page
 *                                            mounts; used as the "we made it"
 *                                            signal for the row-click step.
 *   data-tour="detail-header"             — top header (Back / Export CDD / Raise Escalation)
 *   data-tour="detail-meta"               — meta-bar (Status / Type / Risk / CDD / Staff)
 *   #section-overview … #section-escalations — each scroll section
 */

import { NEXT, BACK, START, DONE, targetExists, advanceWhenAppears } from './_helpers.js';

function waitForElement(selector, timeout = 10000) {
    return new Promise((resolve) => {
        if (document.querySelector(selector)) return resolve();
        const start = Date.now();
        const check = () => {
            if (document.querySelector(selector)) return resolve();
            if (Date.now() - start > timeout) return resolve();
            setTimeout(check, 50);
        };
        check();
    });
}

const sectionStep = (id, sectionId, title, text) => ({
    id,
    attachTo: { element: '#' + sectionId, on: 'right' },
    beforeShowPromise: () => waitForElement('#' + sectionId),
    showOn: targetExists('#' + sectionId),
    title,
    text,
    buttons: [BACK, NEXT],
});

export const clientDetailTour = {
    id: 'client-detail',
    title: 'The detailed client record',
    description: 'A guided walk through every section of a client\'s detail page — so you know where to find each piece of their compliance file.',
    autoLaunchOn: { page: 'clients' },

    steps: [
        {
            id: 'intro',
            title: 'The client detail page',
            text: `Each onboarded client has a detail page that holds their entire
                compliance file — overview, services, CDD, associated persons,
                monitoring, and more. This tour walks through each section so you
                know where to find what you're looking for.`,
            buttons: [START],
        },
        {
            id: 'pick-row',
            attachTo: { element: 'table.aml_table tbody tr.clickable', on: 'top' },
            beforeShowPromise: () => waitForElement('table.aml_table tbody tr.clickable'),
            title: 'Open any client',
            text: `Click any row to open that client's detail page. The tour will
                pick up automatically once you're inside.`,
            // Watch for the detail page's section nav to appear instead of
            // relying on advanceOn-on-row-click (which would only attach to
            // the first matching row).
            when: advanceWhenAppears('[data-tour="section-tab-overview"]'),
        },
        {
            id: 'header',
            attachTo: { element: '[data-tour="detail-header"]', on: 'bottom' },
            beforeShowPromise: () => waitForElement('[data-tour="detail-header"]'),
            title: 'Header — quick actions',
            text: `Top of the page: <strong>Back to Clients</strong> returns you to
                the list, <strong>Export CDD Record</strong> downloads this client's
                full file as a Word document, and <strong>Raise Escalation</strong>
                opens the escalation form pre-filled to this client.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'meta',
            attachTo: { element: '[data-tour="detail-meta"]', on: 'bottom' },
            beforeShowPromise: () => waitForElement('[data-tour="detail-meta"]'),
            title: 'At-a-glance status',
            text: `This section gives you an overall view of the client status —
                overall <strong>Status</strong>, client <strong>Type</strong>,
                <strong>Risk Rating</strong>, <strong>CDD Status</strong> dot,
                and <strong>Assigned Staff</strong>. If the client is
                pre-commencement (existed before AML obligations applied), a badge
                shows here too.`,
            buttons: [BACK, NEXT],
        },
        sectionStep(
            'overview',
            'section-overview',
            'Overview',
            `The client's core record — entity name, type, ABN/ACN, address, contact
                details, assigned staff, former names. <strong>Edit Details</strong>
                opens an inline modal where you can update any of these.`,
        ),
        sectionStep(
            'services',
            'section-services',
            'Services',
            `The designated services this client is being provided. Add new ones as
                the relationship evolves; mark services as Complete when they
                conclude. The set of services drives the level of CDD required.`,
        ),
        sectionStep(
            'cdd',
            'section-cdd',
            'Customer Due Diligence',
            `The heart of the file — run identity verification on the client, see
                the verification result, attach supporting documents, and (for
                entities) confirm the entity check.`,
        ),
        sectionStep(
            'associated',
            'section-associated',
            'Associated Persons',
            `Everyone connected to this client who needs to be on the file — directors,
                beneficial owners, trustees, authorised representatives, etc. Each
                person has their own ID verification record and is screened for PEP /
                sanctions matches.`,
        ),
        sectionStep(
            'ecdd',
            'section-ecdd',
            'Enhanced CDD',
            `Only appears for <strong>High-risk</strong> clients. Captures the extra
                requirements that apply at higher risk: source of funds, source of
                wealth, adverse-media checks, and Senior Manager approval before
                services are provided.`,
        ),
        sectionStep(
            'cdd-review',
            'section-cdd-review',
            'CDD Review',
            `The periodic-review schedule and history. The platform sets the next
                review date based on the client's risk rating; when one falls due,
                a banner at the top of the page prompts you to run the review. The
                review wizard captures who reviewed, what changed, and signs off.`,
        ),
        sectionStep(
            'monitoring',
            'section-monitoring',
            'Monitoring',
            `Free-form monitoring notes — observations, client contact, changes in
                circumstances, and anything else worth recording between formal
                reviews. Notes are immutable once saved (audit trail) and feed into
                the next periodic review.`,
        ),
        sectionStep(
            'documents-ordered',
            'section-documents-ordered',
            'Documents Ordered',
            `Trust deeds, company registrations, and other documents that have been
                ordered for this client through the Macquarie platform. Provides a
                cross-link from the AML record to the document order it produced.`,
        ),
        sectionStep(
            'offboarding',
            'section-offboarding',
            'Offboarding',
            `Where you formally end the client relationship. Records are retained
                for the 7-year compliance period. Active services, escalations and
                associated persons are tidied up automatically when you offboard.`,
        ),
        sectionStep(
            'escalations',
            'section-escalations',
            'Escalations',
            `Every escalation ever raised against this client, in chronological
                order — from initial concern through CO review and (where required)
                Senior Manager sign-off. Read-only summary; raise a new one from
                the header up top.`,
        ),
        {
            id: 'finish',
            title: 'That\'s the client file',
            text: `Each section has its own dedicated tour for deeper coverage —
                Edit Client, Associated Persons, Offboarding, Ongoing Monitoring,
                and so on. You can launch any of them from the Help page.`,
            buttons: [BACK, DONE],
        },
    ],
};
